import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import StaffHeader from '../components/StaffHeader';
import TrialExpired from '../components/TrialExpired';
import usePullToRefresh from '../hooks/usePullToRefresh';
import usePagination from '../hooks/usePagination';
import Pagination from '../components/Pagination';
import PhotoGalleryModal from '../components/PhotoGalleryModal';
import { PRODUCT_TYPE } from '../config/product';
import { formatCurrency } from '../utils/formatCurrency';
import { isDoNotServeConflict, confirmDoNotServe } from '../utils/doNotServe';
import { seasonBadge, seasonAddWarning, addAllSummary, availableStopOptions } from '../utils/seasonConfirm';
import DoNotServeBadge from '../components/DoNotServeBadge';
import usePaymentMethods from '../hooks/usePaymentMethods';
import { methodLabel, markPaidOptions, markPaidDefault } from '../utils/paymentMethods';
import {
  loadRouteRequests, withRequestFlags, requestBadgeText, confirmNoRequest, NO_REQUEST_LABEL, preferenceBadge,
} from '../utils/serviceRequests';
import { companyToday } from '../utils/companyDate';
import DepthEntryModal, { billStopDifference, doorPaymentErrorMessage } from '../components/DepthEntryModal';
import {
  isPendingDepthStop, AWAITING_DEPTH_LABEL, stopDepthBand, snowServiceLabel, stopPremiumPercent, premiumLineLabel,
} from '../utils/stopPrice';

function Routes() {
  const navigate = useNavigate();
  const { isAdmin, isDriver, isTrialExpired, shouldHidePrices, isPro, timezone } = useAuth();

  const [routes, setRoutes] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showStopsModal, setShowStopsModal] = useState(false);
  const [editingRoute, setEditingRoute] = useState(null);
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  // One line inside the stops modal: a season warning after a hand add, or
  // what Add all did.
  const [stopsNotice, setStopsNotice] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [filterView, setFilterView] = useState('active'); // 'active', 'completed', 'archived', 'awaiting_depth'
  // Storm depth entry (depth pricing 3.3): the route whose modal is open.
  const [depthModalRoute, setDepthModalRoute] = useState(null);
  // Bill the difference: the stop being billed, and the last outcome per stop.
  const [billingDiffStopId, setBillingDiffStopId] = useState(null);
  const [diffResults, setDiffResults] = useState({});
  const [routeSortOrder, setRouteSortOrder] = useState(() => {
    return localStorage.getItem('snowtrack_routes_sort') || 'newest';
  });
  const [showTrialModal, setShowTrialModal] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    serviceType: 'snow_removal',
    stormId: '',
    notes: '',
    assignedTo: '',
    driverPayType: '',
    driverPayRate: '',
    driverPayTotal: '',
    driverHoursWorked: '',
    startingAddress: '',
    startingCity: '',
    startingState: '',
    startingZip: '',
    endingAddress: '',
    endingCity: '',
    endingState: '',
    endingZip: '',
    endAtStart: true,
    invoiceTitle: '',
    invoiceMessage: '',
  });
  const [teamMembers, setTeamMembers] = useState([]);
  const [showInvoiceSettings, setShowInvoiceSettings] = useState(false);
  const [showDriverPaySettings, setShowDriverPaySettings] = useState(false);
  const [stopPhotoCounts, setStopPhotoCounts] = useState({});
  const [photoGalleryStopId, setPhotoGalleryStopId] = useState(null);
  const [driverFilter, setDriverFilter] = useState('all'); // 'all', 'unassigned', or a user ID
  const [paymentModal, setPaymentModal] = useState({ open: false, stop: null });
  const paymentMethods = usePaymentMethods();
  const [paymentForm, setPaymentForm] = useState({
    paymentMethod: 'cash',
    paymentDate: companyToday(timezone),
    paymentNotes: '',
    amountCollected: ''
  });
  const [savingPayment, setSavingPayment] = useState(false);
  const [paymentError, setPaymentError] = useState('');
  const [deleteDialog, setDeleteDialog] = useState(null); // { route, text, busy }
  const [archiveSuggest, setArchiveSuggest] = useState(null); // { route, message }
  const [seasonDialog, setSeasonDialog] = useState(null); // { through, preview, text, loading, busy, error }

  const loadData = useCallback(async () => {
    try {
      const [routesRes, customersRes, teamRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_API_URL}/api/routes?includeArchived=true&product=${PRODUCT_TYPE}`),
        axios.get(`${process.env.REACT_APP_API_URL}/api/customers?active=true&includeChildren=true&product=${PRODUCT_TYPE}`),
        axios.get(`${process.env.REACT_APP_API_URL}/api/team`),
      ]);
      setRoutes(routesRes.data);
      setCustomers(customersRes.data);
      setTeamMembers(teamRes.data);
    } catch (err) {
      console.error('Failed to load data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Toggle route sort order and save to localStorage
  const toggleRouteSortOrder = () => {
    const newOrder = routeSortOrder === 'newest' ? 'oldest' : 'newest';
    setRouteSortOrder(newOrder);
    localStorage.setItem('snowtrack_routes_sort', newOrder);
  };

  // Filter and sort routes based on filter view, driver filter, and sort order
  const filteredRoutes = routes
    .filter(route => {
      // Status filter
      if (filterView === 'archived') {
        if (route.is_archived !== true) return false;
      } else if (filterView === 'awaiting_depth') {
        // Depth routes with completed stops still waiting for the storm depth.
        if (route.is_archived || route.pricing_mode !== 'depth' || !(route.pending_depth_count > 0)) return false;
      } else if (filterView === 'completed') {
        if (route.is_archived || route.status !== 'completed') return false;
      } else {
        // 'active' - show not_started and in_progress (non-archived, non-completed)
        if (route.is_archived || route.status === 'completed') return false;
      }

      // Driver filter
      if (driverFilter !== 'all') {
        if (driverFilter === 'unassigned') {
          if (route.assigned_to) return false;
        } else {
          // Filter by specific driver ID
          if (String(route.assigned_to) !== String(driverFilter)) return false;
        }
      }

      return true;
    })
    .sort((a, b) => {
      const dateA = new Date(a.created_at || 0);
      const dateB = new Date(b.created_at || 0);
      return routeSortOrder === 'newest' ? dateB - dateA : dateA - dateB;
    });

  // Pagination for routes (mainly useful for completed/archived views)
  const {
    paginatedData: paginatedRoutes,
    currentPage,
    setCurrentPage,
    itemsPerPage,
    setItemsPerPage,
    totalItems: totalRoutes
  } = usePagination(filteredRoutes, 'routes', 25);

  const { PullToRefreshIndicator } = usePullToRefresh(loadData);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const openAddModal = () => {
    // Check if trial user has exceeded their limit
    if (isTrialExpired()) {
      setShowTrialModal(true);
      return;
    }
    setEditingRoute(null);
    setFormData({
      name: '',
      serviceType: 'snow_removal',
      stormId: '',
      notes: '',
      assignedTo: '',
      driverPayType: '',
      driverPayRate: '',
      driverPayTotal: '',
      driverHoursWorked: '',
      startingAddress: '',
      startingCity: '',
      startingState: '',
      startingZip: '',
      endingAddress: '',
      endingCity: '',
      endingState: '',
      endingZip: '',
      endAtStart: true,
      invoiceTitle: '',
      invoiceMessage: '',
    });
    setShowInvoiceSettings(false);
    setShowDriverPaySettings(false);
    setError('');
    setShowModal(true);
  };

  const openEditModal = (route) => {
    setEditingRoute(route);
    setFormData({
      name: route.name || '',
      serviceType: route.service_type || 'snow_removal',
      stormId: route.storm_id || '',
      notes: route.notes || '',
      assignedTo: route.assigned_to || '',
      driverPayType: route.driver_pay_type || '',
      driverPayRate: route.driver_pay_rate || '',
      driverPayTotal: route.driver_pay_total || '',
      driverHoursWorked: route.driver_hours_worked || '',
      startingAddress: route.starting_address || '',
      startingCity: route.starting_city || '',
      startingState: route.starting_state || '',
      startingZip: route.starting_zip || '',
      endingAddress: route.ending_address || '',
      endingCity: route.ending_city || '',
      endingState: route.ending_state || '',
      endingZip: route.ending_zip || '',
      endAtStart: route.end_at_start ?? true,
      invoiceTitle: route.invoice_title || '',
      invoiceMessage: route.invoice_message || '',
    });
    setShowInvoiceSettings(!!(route.invoice_title || route.invoice_message));
    setShowDriverPaySettings(!!(route.driver_pay_type || route.driver_pay_rate));
    setError('');
    setShowModal(true);
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (type === 'checkbox') {
      setFormData((prev) => ({
        ...prev,
        [name]: checked,
      }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    try {
      // Calculate driver pay total based on pay type
      let calculatedPayTotal = formData.driverPayTotal || null;
      if (formData.driverPayType === 'hourly' && formData.driverPayRate && formData.driverHoursWorked) {
        calculatedPayTotal = parseFloat(formData.driverPayRate) * parseFloat(formData.driverHoursWorked);
      } else if (formData.driverPayType === 'flat' && formData.driverPayRate) {
        calculatedPayTotal = parseFloat(formData.driverPayRate);
      } else if (formData.driverPayType === 'per_stop' && formData.driverPayRate && editingRoute?.stop_count) {
        calculatedPayTotal = parseFloat(formData.driverPayRate) * parseInt(editingRoute.stop_count);
      }

      const payload = {
        name: formData.name,
        serviceType: formData.serviceType,
        stormId: formData.stormId,
        notes: formData.notes,
        assignedTo: formData.assignedTo || null,
        startingAddress: formData.startingAddress,
        startingCity: formData.startingCity,
        startingState: formData.startingState,
        startingZip: formData.startingZip,
        endingAddress: formData.endAtStart ? formData.startingAddress : formData.endingAddress,
        endingCity: formData.endAtStart ? formData.startingCity : formData.endingCity,
        endingState: formData.endAtStart ? formData.startingState : formData.endingState,
        endingZip: formData.endAtStart ? formData.startingZip : formData.endingZip,
        endAtStart: formData.endAtStart,
        invoiceTitle: formData.invoiceTitle || null,
        invoiceMessage: formData.invoiceMessage || null,
        driverPayType: formData.driverPayType || null,
        driverPayRate: formData.driverPayRate || null,
        driverPayTotal: calculatedPayTotal,
        driverHoursWorked: formData.driverHoursWorked || null,
      };

      if (editingRoute) {
        await axios.put(
          `${process.env.REACT_APP_API_URL}/api/routes/${editingRoute.id}`,
          payload
        );
      } else {
        await axios.post(`${process.env.REACT_APP_API_URL}/api/routes`, payload);
      }

      setShowModal(false);
      loadData();
    } catch (err) {
      // Check for trial expired error
      if (err.response?.data?.code === 'TRIAL_EXPIRED') {
        setShowModal(false);
        setShowTrialModal(true);
        return;
      }
      setError(err.response?.data?.error || 'Failed to save route');
    } finally {
      setSaving(false);
    }
  };

  // A route with completed stops cannot be deleted (the server answers 409
  // ROUTE_HAS_COMPLETED_STOPS): its stops carry the prices every balance and
  // statement reads. Delete on such a route opens the archive suggestion; any
  // other route gets a type-the-name confirmation first.
  const deleteRoute = (route) => {
    if (parseInt(route.completed_count || 0, 10) > 0) {
      setArchiveSuggest({ route, message: '' });
      return;
    }
    setDeleteDialog({ route, text: '', busy: false });
  };

  const confirmDeleteRoute = async () => {
    const { route } = deleteDialog;
    setDeleteDialog({ ...deleteDialog, busy: true });
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/api/routes/${route.id}`);
      setDeleteDialog(null);
      loadData();
      setShowModal(false);
      setSuccessMessage(`Route "${route.name}" deleted`);
    } catch (err) {
      setDeleteDialog(null);
      if (err.response?.status === 409) {
        setArchiveSuggest({ route, message: err.response.data?.error || '' });
        loadData();
        return;
      }
      setError(err.response?.data?.error || 'Failed to delete route');
    }
  };

  const archiveInstead = async () => {
    const { route } = archiveSuggest;
    setArchiveSuggest(null);
    try {
      await axios.put(`${process.env.REACT_APP_API_URL}/api/routes/${route.id}`, { isArchived: true });
      loadData();
      setSuccessMessage(`Route "${route.name}" archived. Its completed stops still count in balances, statements and reports.`);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to archive route');
    }
  };

  // ARCHIVE SEASON: every non-archived route whose stops were all done on or
  // before the chosen date (default the most recent Apr 30). The server
  // previews the list, the owner types the count, and only those ids go back.
  const loadSeasonPreview = async (through) => {
    setSeasonDialog((d) => ({ ...(d || {}), through, loading: true, error: '', text: '', preview: null }));
    try {
      const params = through ? `?through=${encodeURIComponent(through)}` : '';
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/routes/archive-season${params}`);
      setSeasonDialog((d) => ({ ...d, through: response.data.through, loading: false, preview: response.data }));
    } catch (err) {
      setSeasonDialog((d) => ({ ...d, loading: false, error: err.response?.data?.error || 'Failed to find routes to archive' }));
    }
  };

  const confirmArchiveSeason = async () => {
    const { through, preview } = seasonDialog;
    setSeasonDialog({ ...seasonDialog, busy: true });
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/routes/archive-season`, {
        through,
        routeIds: preview.routes.map((r) => r.id),
      });
      setSeasonDialog(null);
      loadData();
      setSuccessMessage(`${response.data.archived} route${response.data.archived === 1 ? '' : 's'} archived. Balances, statements and reports are unchanged.`);
    } catch (err) {
      setSeasonDialog({ ...seasonDialog, busy: false, error: err.response?.data?.error || 'Failed to archive routes' });
    }
  };

  const seasonConfirmPhrase = seasonDialog?.preview ? `ARCHIVE ${seasonDialog.preview.count}` : '';

  const archiveRoute = async (route) => {
    const action = route.is_archived ? 'restore' : 'archive';
    if (!window.confirm(`${route.is_archived ? 'Restore' : 'Archive'} "${route.name}"?`)) {
      return;
    }

    try {
      // Sends isArchived and nothing else. This used to spread the raw route row;
      // since PUT /api/routes/:id was rebuilt around presence testing every
      // snake_case key in that spread failed has() and only isArchived applied, so
      // the spread was already inert - but it was a loaded gun pointed at the same
      // foot the customer PUT was just shot with. Removed rather than left.
      await axios.put(`${process.env.REACT_APP_API_URL}/api/routes/${route.id}`, {
        isArchived: !route.is_archived,
      });
      loadData();
      setSuccessMessage(`Route ${action}d successfully`);
    } catch (err) {
      setError(err.response?.data?.error || `Failed to ${action} route`);
    }
  };

  const openStopsModal = async (route) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/routes/${route.id}`
      );
      setSelectedRoute(response.data);
      setStopsNotice('');
      setRouteRequests(null);
      setShowStopsModal(true);
      // Which on-call customers asked for this route's day (null on failure:
      // then nothing is greyed).
      loadRouteRequests(route.id).then(setRouteRequests);

      // Load photo counts for completed stops
      const completedStops = response.data.stops?.filter(s => s.status === 'completed') || [];
      if (completedStops.length > 0) {
        const stopIds = completedStops.map(s => s.id);
        try {
          const countsRes = await axios.post(
            `${process.env.REACT_APP_API_URL}/api/route-stops/photo-counts`,
            { stopIds }
          );
          setStopPhotoCounts(countsRes.data);
        } catch (photoErr) {
          console.error('Failed to load photo counts:', photoErr);
        }
      }
    } catch (err) {
      console.error('Failed to load route details:', err);
    }
  };

  const addStopToRoute = async (customerId, option = null) => {
    if (!selectedRoute) return;
    setStopsNotice('');
    // An on-call customer who has not asked for this day: ask first.
    if (!confirmNoRequest(option?.request)) return;

    try {
      const currentStopCount = selectedRoute.stops?.length || 0;
      const body = { customerId, stopOrder: currentStopCount + 1 };
      const url = `${process.env.REACT_APP_API_URL}/api/routes/${selectedRoute.id}/stops`;
      try {
        await axios.post(url, body);
      } catch (err) {
        // A flagged customer: ask once, and add only on a yes.
        if (!isDoNotServeConflict(err)) throw err;
        if (!confirmDoNotServe(err)) return;
        await axios.post(url, { ...body, confirmDoNotServe: true });
      }
      // Declined or no reply for the season: added, with a one-line warning.
      setStopsNotice(seasonAddWarning(option) || '');
      // Reload route details
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/routes/${selectedRoute.id}`
      );
      setSelectedRoute(response.data);
      loadData(); // Refresh routes list
    } catch (err) {
      setError('Failed to add stop');
    }
  };

  // Add all: every customer not on the route, skipping Do Not Serve, those who
  // declined the season and (depth route) those with no size tier. The server
  // decides (POST /api/routes/:id/stops/add-all) and says what it skipped.
  const [addingAll, setAddingAll] = useState(false);
  // Declined customers are hidden from Available until this is ticked.
  const [showDeclined, setShowDeclined] = useState(false);
  // GET /api/routes/:id/service-requests for the open stops modal.
  const [routeRequests, setRouteRequests] = useState(null);
  const addAllToRoute = async () => {
    if (!selectedRoute) return;
    setAddingAll(true);
    setStopsNotice('');
    try {
      const res = await axios.post(`${process.env.REACT_APP_API_URL}/api/routes/${selectedRoute.id}/stops/add-all`);
      setStopsNotice(addAllSummary(res.data));
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/routes/${selectedRoute.id}`);
      setSelectedRoute(response.data);
      loadData();
    } catch (err) {
      setStopsNotice(err.response?.data?.error || 'Failed to add stops');
    } finally {
      setAddingAll(false);
    }
  };

  const removeStopFromRoute = async (stop) => {
    if (!selectedRoute) return;
    if (stop.status === 'completed') return;
    const stopId = stop.id;
    if (!window.confirm('Remove this stop from the route?')) return;

    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/api/routes/${selectedRoute.id}/stops/${stopId}`
      );
      // Reload route details
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/routes/${selectedRoute.id}`
      );
      setSelectedRoute(response.data);
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to remove stop');
    }
  };

  // Bill the difference on a stop whose invoice went out before a depth
  // change raised its price. The route is reloaded so the button goes away.
  const handleBillDifference = async (stop) => {
    setBillingDiffStopId(stop.id);
    const outcome = await billStopDifference(stop.id);
    setDiffResults(prev => ({ ...prev, [stop.id]: outcome }));
    setBillingDiffStopId(null);
    if (selectedRoute) {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/routes/${selectedRoute.id}`);
        setSelectedRoute(response.data);
      } catch (err) {
        console.error('Failed to reload route:', err);
      }
    }
  };

  // Open payment modal for a stop
  const openPaymentModal = (stop) => {
    setPaymentForm({
      paymentMethod: markPaidDefault(paymentMethods, stop.payment_method),
      paymentDate: companyToday(timezone),
      paymentNotes: '',
      amountCollected: ''
    });
    setPaymentError('');
    setPaymentModal({ open: true, stop });
  };

  // Submit payment update
  const submitPayment = async () => {
    if (!paymentModal.stop?.id) return;

    // A stop awaiting the storm depth has no price yet: the amount collected
    // is required, and Square cannot be used (design 3.4).
    const pendingDepth = isPendingDepthStop(paymentModal.stop);
    const amount = parseFloat(paymentForm.amountCollected);
    // Square on a stop awaiting depth is "invoice after depth": the method is
    // recorded as not paid with no amount, and the invoice goes out once the
    // depth prices the stop (Bill All, or the auto-invoice after depth).
    const invoiceAfterDepth = pendingDepth && paymentForm.paymentMethod === 'square';
    if (pendingDepth && !invoiceAfterDepth && !(amount > 0)) {
      setPaymentError('This stop is waiting for the storm depth, so enter the amount you collected.');
      return;
    }

    setSavingPayment(true);
    setPaymentError('');
    try {
      await axios.put(
        `${process.env.REACT_APP_API_URL}/api/route-stops/${paymentModal.stop.id}/payment`,
        {
          paymentStatus: invoiceAfterDepth ? 'not_paid' : 'paid',
          paymentMethod: paymentForm.paymentMethod,
          paymentDate: invoiceAfterDepth ? undefined : paymentForm.paymentDate,
          paymentNotes: paymentForm.paymentNotes || undefined,
          amountPaid: pendingDepth && !invoiceAfterDepth ? amount : undefined
        }
      );
      setPaymentModal({ open: false, stop: null });
      // Reload route details
      if (selectedRoute) {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/routes/${selectedRoute.id}`
        );
        setSelectedRoute(response.data);
      }
      loadData();
    } catch (err) {
      console.error('Failed to update payment:', err);
      const doorMessage = doorPaymentErrorMessage(err);
      if (doorMessage) {
        setPaymentError(doorMessage);
        return;
      }
      setError('Failed to update payment status');
    } finally {
      setSavingPayment(false);
    }
  };

  // Flatten customers and their child properties into a single list of stops
  const getAllStopOptions = () => {
    const stopOptions = [];
    for (const customer of customers) {
      // Add parent customer as a stop option
      stopOptions.push({
        id: customer.id,
        displayName: `${customer.first_name} ${customer.last_name}`,
        address: customer.address,
        city: customer.city,
        service_frequency: customer.service_frequency,
        snowfall_threshold: customer.snowfall_threshold,
        isChild: false,
        doNotServe: customer.do_not_serve === true,
        doNotServeReason: customer.do_not_serve_reason,
        seasonStatus: customer.season_status || null,
        seasonKey: customer.season_key || null,
        // Service requests are kept on the root customer.
        servicePreference: customer.service_preference,
        rootId: customer.id,
      });
      // Add child properties as separate stop options
      if (customer.properties && customer.properties.length > 0) {
        for (const prop of customer.properties) {
          if (prop.is_active) {
            stopOptions.push({
              id: prop.id,
              displayName: `${customer.first_name} ${customer.last_name} - ${prop.address}`,
              address: prop.address,
              city: prop.city,
              service_frequency: prop.service_frequency,
              snowfall_threshold: customer.snowfall_threshold, // Child inherits from parent
              isChild: true,
              parentName: `${customer.first_name} ${customer.last_name}`,
              // Resolved from the parent by the server.
              doNotServe: prop.do_not_serve === true,
              doNotServeReason: prop.do_not_serve_reason,
              // The parent's season answer, as the server resolves it.
              seasonStatus: prop.season_status || customer.season_status || null,
              seasonKey: prop.season_key || customer.season_key || null,
              // A property follows its root for on-call and requests.
              servicePreference: customer.service_preference,
              rootId: customer.id,
            });
          }
        }
      }
    }
    return stopOptions;
  };

  const getCustomersNotInRoute = () => {
    const allStops = getAllStopOptions();
    if (!selectedRoute || !selectedRoute.stops) return allStops;
    const stopCustomerIds = selectedRoute.stops.map((s) => s.customer_id);
    return allStops.filter((s) => !stopCustomerIds.includes(s.id));
  };

  const [optimizing, setOptimizing] = useState(false);

  // Route optimization using Google Maps Distance Matrix API
  const optimizeRouteOrder = async () => {
    if (!selectedRoute?.stops?.length) return;

    setOptimizing(true);
    try {
      // Call the server endpoint for Google Maps optimization
      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/routes/${selectedRoute.id}/optimize`
      );

      // Reload route to get new order
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/routes/${selectedRoute.id}`
      );
      setSelectedRoute(response.data);
      setSuccessMessage('Route optimized using Google Maps! Stops are now in the most efficient order.');
    } catch (err) {
      console.error('Failed to optimize route:', err);
      // Fall back to simple sort if Google Maps fails
      if (err.response?.status === 500) {
        setError('Google Maps optimization failed. Using basic sorting instead.');
        await fallbackOptimize();
      } else {
        setError('Failed to optimize route order');
      }
    } finally {
      setOptimizing(false);
    }
  };

  // Fallback to simple sort if Google Maps API fails
  const fallbackOptimize = async () => {
    const sortedStops = [...selectedRoute.stops].sort((a, b) => {
      const cityCompare = (a.city || '').localeCompare(b.city || '');
      if (cityCompare !== 0) return cityCompare;
      const zipCompare = (a.zip || '').localeCompare(b.zip || '');
      if (zipCompare !== 0) return zipCompare;
      const streetA = (a.address || '').replace(/^\d+\s*/, '');
      const streetB = (b.address || '').replace(/^\d+\s*/, '');
      return streetA.localeCompare(streetB);
    });

    try {
      for (let i = 0; i < sortedStops.length; i++) {
        await axios.put(
          `${process.env.REACT_APP_API_URL}/api/routes/${selectedRoute.id}/stops/${sortedStops[i].id}`,
          { stopOrder: i + 1 }
        );
      }
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/routes/${selectedRoute.id}`
      );
      setSelectedRoute(response.data);
    } catch (err) {
      console.error('Fallback optimization failed:', err);
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'in_progress':
        return 'status-badge in-progress';
      case 'completed':
        return 'status-badge completed';
      default:
        return 'status-badge not-started';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'in_progress':
        return 'In Progress';
      case 'completed':
        return 'Completed';
      default:
        return 'Not Started';
    }
  };

  // The stops modal's Available list (declined hidden unless Show declined).
  const notInRoute = getCustomersNotInRoute();
  const { visible: unsorted, declinedCount } = availableStopOptions(notInRoute, showDeclined);
  // On-call customers who asked come first; those who did not are greyed last.
  const visible = withRequestFlags(routeRequests, unsorted);

  return (
    <div className="routes-page">
      {/* Trial Expired Modal */}
      {showTrialModal && (
        <TrialExpired
          variant="modal"
          onClose={() => setShowTrialModal(false)}
        />
      )}
      {/* Header */}
      <StaffHeader active="routes" />

      <PullToRefreshIndicator />

      {error && (
        <div className="error-banner" style={{ background: '#fef2f2', color: '#dc2626', padding: '12px 16px', margin: '0 16px 12px', borderRadius: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', border: '1px solid #fecaca' }}>
          <span>{error}</span>
          <button onClick={() => setError('')} style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: '1.2rem', padding: '0 4px' }}>&times;</button>
        </div>
      )}
      {successMessage && (
        <div className="success-banner" style={{ background: '#f0fdf4', color: '#16a34a', padding: '12px 16px', margin: '0 16px 12px', borderRadius: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', border: '1px solid #bbf7d0' }}>
          <span>{successMessage}</span>
          <button onClick={() => setSuccessMessage('')} style={{ background: 'none', border: 'none', color: '#16a34a', cursor: 'pointer', fontSize: '1.2rem', padding: '0 4px' }}>&times;</button>
        </div>
      )}

      <div className="page-container">
        {/* Page Header */}
        <div className="page-header flex-between">
          <div>
            <h1 className="page-title">Routes</h1>
            <p className="text-muted">{filterView === 'awaiting_depth'
              ? `${filteredRoutes.length} route${filteredRoutes.length !== 1 ? 's' : ''} awaiting depth`
              : `${filteredRoutes.length} ${filterView} route${filteredRoutes.length !== 1 ? 's' : ''}`}</p>
          </div>
          <div className="header-actions">
            {isAdmin() && (
              <button className="btn btn-outline" onClick={() => loadSeasonPreview('')}>
                Archive season
              </button>
            )}
            {isAdmin() && filterView === 'active' && (
              <button className="btn btn-primary" onClick={openAddModal}>
                + New Route
              </button>
            )}
          </div>
        </div>

        {/* Filter and Sort Controls */}
        <div className="routes-controls">
          <div className="archive-toggle">
            <button
              className={`toggle-btn ${filterView === 'active' ? 'active' : ''}`}
              onClick={() => setFilterView('active')}
            >
              Active
            </button>
            <button
              className={`toggle-btn ${filterView === 'completed' ? 'active' : ''}`}
              onClick={() => setFilterView('completed')}
            >
              Completed
            </button>
            <button
              className={`toggle-btn ${filterView === 'archived' ? 'active' : ''}`}
              onClick={() => setFilterView('archived')}
            >
              Archived
            </button>
            {isAdmin() && routes.some(r => r.pricing_mode === 'depth') && (
              <button
                className={`toggle-btn ${filterView === 'awaiting_depth' ? 'active' : ''}`}
                onClick={() => setFilterView('awaiting_depth')}
              >
                Awaiting depth
              </button>
            )}
          </div>
          <button className="sort-toggle" onClick={toggleRouteSortOrder}>
            {routeSortOrder === 'newest' ? '↓ Newest' : '↑ Oldest'}
          </button>
          {isAdmin() && teamMembers.length > 0 && (
            <select
              className="driver-filter-select"
              value={driverFilter}
              onChange={(e) => setDriverFilter(e.target.value)}
            >
              <option value="all">All Drivers</option>
              <option value="unassigned">Unassigned</option>
              {teamMembers.map(member => (
                <option key={member.id} value={member.id}>
                  {member.first_name} {member.last_name}
                </option>
              ))}
            </select>
          )}
        </div>

        {/* Routes List */}
        {loading ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
          </div>
        ) : filteredRoutes.length === 0 ? (
          <div className="empty-state">
            <div style={{ fontSize: '3rem', marginBottom: '12px', opacity: 0.5 }}>🗺️</div>
            <h3 style={{ margin: '0 0 8px', color: 'var(--color-text)' }}>
              {filterView === 'archived' && 'No archived routes'}
                {filterView === 'awaiting_depth' && 'No routes awaiting depth'}
              {filterView === 'completed' && 'No completed routes'}
              {filterView === 'active' && 'No routes yet'}
            </h3>
            <p style={{ color: 'var(--color-text-light)', margin: '0 0 16px' }}>
              {filterView === 'active' && routes.length === 0 ? 'Create a route to start tracking your snow removal services' : 'No routes match this filter'}
            </p>
            {isAdmin() && filterView === 'active' && routes.length === 0 && (
              <button className="btn btn-primary" onClick={openAddModal}>
                + Create Route
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="routes-list">
              {paginatedRoutes.map((route) => (
              <div key={route.id} className={`route-card ${route.is_archived ? 'archived' : ''}`}>
                <div className="route-card-header">
                  <div className="route-info">
                    <h3 className="route-name">
                      {route.name}
                      {route.is_archived && <span className="archived-badge">Archived</span>}
                    </h3>
                    <div className="route-meta">
                      <span className={getStatusBadgeClass(route.status)}>
                        {getStatusLabel(route.status)}
                      </span>
                      {route.storm_id && <span className="storm-id-badge">Storm: {route.storm_id}</span>}
                      {route.assigned_to_name ? (
                        <span className="assigned-badge">{route.assigned_to_name}</span>
                      ) : isAdmin() && (
                        <span className="assigned-badge unassigned">Unassigned</span>
                      )}
                      <span className="stop-count">{route.stop_count || 0} stops</span>
                      {!shouldHidePrices() && (
                        <span className="route-total-wrapper">
                          {/* Never $0.00 because of stops awaiting depth: with
                              nothing priced, the slot reads the label, muted
                              rather than green, beside the amber depth chip. */}
                          {route.pricing_mode === 'depth' && route.pending_depth_count > 0 && parseFloat(route.total_price || 0) === 0 ? (
                            <span className="route-total route-total-muted">{AWAITING_DEPTH_LABEL}</span>
                          ) : (
                            <span className="route-total">{formatCurrency(route.total_price)}</span>
                          )}
                          {route.pricing_mode === 'depth' && route.pending_depth_count > 0 && parseFloat(route.total_price || 0) !== 0 && (
                            <span className="awaiting-depth-indicator">+ {route.pending_depth_count} awaiting depth</span>
                          )}
                          {parseInt(route.skipped_count) > 0 && (
                            <span className="skipped-indicator">({route.skipped_count} skipped)</span>
                          )}
                        </span>
                      )}
                      {route.pricing_mode === 'depth' && (() => {
                        const pending = route.pending_depth_count || 0;
                        const hasDepth = route.snow_depth_inches !== null && route.snow_depth_inches !== undefined;
                        const overrides = route.depth_override_count || 0;
                        // Anything waiting: amber, opens the modal. A depth
                        // entered with nothing waiting: grey, opens it to change.
                        const label = pending > 0
                          ? `Awaiting depth · ${pending} stop${pending === 1 ? '' : 's'}`
                          : hasDepth
                          ? `${Number(route.snow_depth_inches).toFixed(1)} in${overrides > 0 ? ` · ${overrides} city override${overrides === 1 ? '' : 's'}` : ''}`
                          : 'Depth not entered';
                        const tone = pending > 0 ? 'awaiting' : hasDepth ? 'set' : 'empty';
                        return isAdmin() ? (
                          <button
                            type="button"
                            className={`depth-chip depth-chip-${tone}`}
                            onClick={() => setDepthModalRoute(route)}
                            title={hasDepth ? 'Change the storm depth' : 'Enter the storm depth'}
                          >
                            {label}
                          </button>
                        ) : (
                          <span className={`depth-chip depth-chip-${tone}`}>{label}</span>
                        );
                      })()}
                      {isDriver() && route.driver_pay_type && (route.driver_pay_total || route.driver_pay_rate) && (
                        <span className="driver-pay-badge">
                          Your pay: {route.driver_pay_total
                            ? `${formatCurrency(route.driver_pay_total)}`
                            : route.driver_pay_type === 'flat'
                              ? `${formatCurrency(route.driver_pay_rate)}`
                              : route.driver_pay_type === 'hourly'
                                ? `${formatCurrency(route.driver_pay_rate)}/hr`
                                : `${formatCurrency(route.driver_pay_rate)}/stop`
                          }
                        </span>
                      )}
                      {/* Payment breakdown for completed routes */}
                      {!shouldHidePrices() && route.status === 'completed' && parseFloat(route.total_price || 0) > 0 && (
                        <span className="payment-breakdown">
                          {parseFloat(route.total_unpaid || 0) === 0 && route.pending_depth_count > 0 ? (
                            <span className="payment-paid">{formatCurrency(route.total_paid)} paid</span>
                          ) : parseFloat(route.total_unpaid || 0) === 0 ? (
                            <span className="payment-fully-paid">✓ Paid</span>
                          ) : parseFloat(route.total_paid || 0) === 0 ? (
                            <span className="payment-unpaid">{formatCurrency(route.total_unpaid)} unpaid</span>
                          ) : (
                            <>
                              <span className="payment-paid">{formatCurrency(route.total_paid)}</span>
                              <span className="payment-separator">|</span>
                              <span className="payment-unpaid">{formatCurrency(route.total_unpaid)}</span>
                            </>
                          )}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="route-actions">
                    {!route.is_archived ? (
                      <>
                        <button
                          className="btn btn-sm btn-secondary"
                          onClick={() => navigate('/storm-mode', { state: { routeId: route.id } })}
                        >
                          {route.status === 'completed' ? 'View' : route.status === 'in_progress' ? 'Continue' : 'Start'}
                        </button>
                        <button
                          className="btn btn-sm btn-outline"
                          onClick={() => openStopsModal(route)}
                        >
                          Stops
                        </button>
                        {isAdmin() && (
                          <>
                            <button
                              className="btn btn-sm btn-outline"
                              onClick={() => openEditModal(route)}
                            >
                              Edit
                            </button>
                            <button
                              className="btn btn-sm btn-outline"
                              onClick={() => archiveRoute(route)}
                              title="Archive route"
                            >
                              Archive
                            </button>
                            <button
                              className="btn btn-sm btn-danger"
                              onClick={() => deleteRoute(route)}
                              title="Delete route"
                              aria-label="Delete route"
                            >
                              Delete
                            </button>
                          </>
                        )}
                      </>
                    ) : (
                      /* Archived route actions */
                      <>
                        <button
                          className="btn btn-sm btn-outline"
                          onClick={() => openStopsModal(route)}
                        >
                          View Stops
                        </button>
                        {isAdmin() && (
                          <>
                            <button
                              className="btn btn-sm btn-primary"
                              onClick={() => archiveRoute(route)}
                            >
                              Restore
                            </button>
                            <button
                              className="btn btn-sm btn-danger"
                              onClick={() => deleteRoute(route)}
                              aria-label="Delete route"
                            >
                              Delete
                            </button>
                          </>
                        )}
                      </>
                    )}
                  </div>
                </div>
                {route.notes && (
                  <p className="route-notes">{route.notes}</p>
                )}
              </div>
              ))}
            </div>
            <Pagination
              currentPage={currentPage}
              totalItems={totalRoutes}
              itemsPerPage={itemsPerPage}
              onPageChange={setCurrentPage}
              onItemsPerPageChange={setItemsPerPage}
            />
          </>
        )}
      </div>

      {/* Add/Edit Route Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <div className="modal-header">
              <h2>{editingRoute ? 'Edit Route' : 'New Route'}</h2>
              <button className="modal-close" onClick={() => setShowModal(false)} aria-label="Close">&times;</button>
            </div>

            <form onSubmit={handleSubmit}>
              {error && <div className="alert alert-error" aria-live="polite">{error}</div>}

              <div className="modal-body">
                <div className="form-group">
                  <label className="form-label">Route Name *</label>
                  <input
                    type="text"
                    name="name"
                    className="form-input"
                    value={formData.name}
                    onChange={handleFormChange}
                    placeholder="e.g., North Side, Downtown, etc."
                    required
                  />
                </div>

                {/* Service Type - Snow Removal only for this app */}
                <input type="hidden" name="serviceType" value="snow_removal" />

                <div className="form-group">
                  <label className="form-label">Storm ID</label>
                  <input
                    type="text"
                    name="stormId"
                    className="form-input"
                    value={formData.stormId}
                    onChange={handleFormChange}
                    placeholder="e.g., 11.29.25"
                  />
                  <small className="text-muted">Group routes by storm for referral discounts</small>
                </div>

                <div className="form-group">
                  <label className="form-label">Notes</label>
                  <textarea
                    name="notes"
                    className="form-textarea"
                    value={formData.notes}
                    onChange={handleFormChange}
                    rows={3}
                    placeholder="Any special instructions for this route..."
                  />
                </div>

                {teamMembers.length > 0 && (
                  <div className="form-group">
                    <label className="form-label">Assign To</label>
                    <select
                      name="assignedTo"
                      className="form-select"
                      value={formData.assignedTo}
                      onChange={handleFormChange}
                    >
                      <option value="">Unassigned</option>
                      {teamMembers.map(member => (
                        <option key={member.id} value={member.id}>
                          {member.first_name} {member.last_name}
                          {member.role && ` (${member.role.replace('_', ' ')})`}
                        </option>
                      ))}
                    </select>
                    <small className="text-muted">Assign this route to a team member</small>
                  </div>
                )}

                {/* Driver Pay Settings - Owner, Admin, and Crew Lead */}
                {(isAdmin() || (!isDriver())) && (
                  <>
                    <div
                      className="form-divider collapsible-header"
                      onClick={() => setShowDriverPaySettings(!showDriverPaySettings)}
                      style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                    >
                      <span>Driver Pay (Optional)</span>
                      <span style={{ fontSize: '0.8rem' }}>{showDriverPaySettings ? '▼' : '▶'}</span>
                    </div>

                    {showDriverPaySettings && (
                      <>
                        <p className="form-hint">Set driver pay for this route. Drivers will see their earnings on their route history.</p>

                        <div className="form-row">
                          <div className="form-group">
                            <label className="form-label">Pay Type</label>
                            <select
                              name="driverPayType"
                              className="form-select"
                              value={formData.driverPayType}
                              onChange={handleFormChange}
                            >
                              <option value="">None</option>
                              <option value="flat">Flat Rate</option>
                              <option value="hourly">Hourly</option>
                              <option value="per_stop">Per Stop</option>
                            </select>
                          </div>
                          <div className="form-group">
                            <label className="form-label">
                              {formData.driverPayType === 'hourly' ? 'Hourly Rate ($)' :
                               formData.driverPayType === 'per_stop' ? 'Rate Per Stop ($)' :
                               'Pay Rate ($)'}
                            </label>
                            <input
                              type="number"
                              name="driverPayRate"
                              className="form-input"
                              value={formData.driverPayRate}
                              onChange={handleFormChange}
                              placeholder="0.00"
                              step="0.01"
                              min="0"
                              disabled={!formData.driverPayType}
                            />
                          </div>
                        </div>

                        {formData.driverPayType === 'hourly' && (
                          <div className="form-row">
                            <div className="form-group">
                              <label className="form-label">Hours Worked</label>
                              <input
                                type="number"
                                name="driverHoursWorked"
                                className="form-input"
                                value={formData.driverHoursWorked}
                                onChange={handleFormChange}
                                placeholder="0.0"
                                step="0.25"
                                min="0"
                              />
                            </div>
                            <div className="form-group">
                              <label className="form-label">Calculated Total</label>
                              <div className="calculated-total">
                                ${formData.driverPayRate && formData.driverHoursWorked
                                  ? (parseFloat(formData.driverPayRate) * parseFloat(formData.driverHoursWorked)).toFixed(2)
                                  : '0.00'}
                              </div>
                            </div>
                          </div>
                        )}

                        {formData.driverPayType === 'flat' && formData.driverPayRate && (
                          <div className="form-group">
                            <label className="form-label">Total Pay</label>
                            <div className="calculated-total">
                              ${parseFloat(formData.driverPayRate).toFixed(2)}
                            </div>
                          </div>
                        )}

                        {formData.driverPayType === 'per_stop' && formData.driverPayRate && editingRoute && (
                          <div className="form-group">
                            <label className="form-label">Estimated Total ({editingRoute.stop_count || 0} stops)</label>
                            <div className="calculated-total">
                              ${(parseFloat(formData.driverPayRate) * (parseInt(editingRoute.stop_count) || 0)).toFixed(2)}
                            </div>
                            <small className="text-muted">Final total calculated based on completed stops</small>
                          </div>
                        )}
                      </>
                    )}
                  </>
                )}

                <div className="form-divider">Starting Location</div>
                <p className="form-hint">Where does the driver start from? (home, shop, etc.)</p>

                <div className="form-group">
                  <label className="form-label">Starting Address</label>
                  <input
                    type="text"
                    name="startingAddress"
                    className="form-input"
                    value={formData.startingAddress}
                    onChange={handleFormChange}
                    placeholder="123 Main St"
                  />
                </div>

                <div className="form-row form-row-3">
                  <div className="form-group">
                    <label className="form-label">City</label>
                    <input
                      type="text"
                      name="startingCity"
                      className="form-input"
                      value={formData.startingCity}
                      onChange={handleFormChange}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">State</label>
                    <input
                      type="text"
                      name="startingState"
                      className="form-input"
                      value={formData.startingState}
                      onChange={handleFormChange}
                      maxLength={2}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">ZIP</label>
                    <input
                      type="text"
                      name="startingZip"
                      className="form-input"
                      value={formData.startingZip}
                      onChange={handleFormChange}
                    />
                  </div>
                </div>

                <div className="form-divider">Ending Location</div>

                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="endAtStart"
                      checked={formData.endAtStart}
                      onChange={handleFormChange}
                    />
                    End at starting location
                  </label>
                </div>

                {!formData.endAtStart && (
                  <>
                    <div className="form-group">
                      <label className="form-label">Ending Address</label>
                      <input
                        type="text"
                        name="endingAddress"
                        className="form-input"
                        value={formData.endingAddress}
                        onChange={handleFormChange}
                        placeholder="456 Other St"
                      />
                    </div>

                    <div className="form-row form-row-3">
                      <div className="form-group">
                        <label className="form-label">City</label>
                        <input
                          type="text"
                          name="endingCity"
                          className="form-input"
                          value={formData.endingCity}
                          onChange={handleFormChange}
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label">State</label>
                        <input
                          type="text"
                          name="endingState"
                          className="form-input"
                          value={formData.endingState}
                          onChange={handleFormChange}
                          maxLength={2}
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label">ZIP</label>
                        <input
                          type="text"
                          name="endingZip"
                          className="form-input"
                          value={formData.endingZip}
                          onChange={handleFormChange}
                        />
                      </div>
                    </div>
                  </>
                )}

                {/* Invoice Settings - Collapsible */}
                <div
                  className="form-divider collapsible-header"
                  onClick={() => setShowInvoiceSettings(!showInvoiceSettings)}
                  style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                >
                  <span>Invoice Settings (Optional)</span>
                  <span style={{ fontSize: '0.8rem' }}>{showInvoiceSettings ? '▼' : '▶'}</span>
                </div>

                {showInvoiceSettings && (
                  <>
                    <p className="form-hint">Customize invoice title and message for this route. Leave blank to use company defaults.</p>

                    <div className="form-group">
                      <label className="form-label">Invoice Title</label>
                      <input
                        type="text"
                        name="invoiceTitle"
                        className="form-input"
                        value={formData.invoiceTitle}
                        onChange={handleFormChange}
                        placeholder="e.g., Snow Service - North Side (defaults to route name)"
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">Invoice Message</label>
                      <textarea
                        name="invoiceMessage"
                        className="form-textarea"
                        value={formData.invoiceMessage}
                        onChange={handleFormChange}
                        rows={3}
                        placeholder="Thank you message for customers..."
                      />
                      <small className="text-muted">
                        Variables: {'{route_name}'}, {'{service_date}'}, {'{customer_name}'}
                      </small>
                    </div>
                  </>
                )}

              </div>

              <div className="modal-footer">
                <button type="button" className="btn btn-outline" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Saving...' : editingRoute ? 'Save Changes' : 'Create Route'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Manage Stops Modal */}
      {showStopsModal && selectedRoute && (
        <div className="modal-overlay" onClick={() => setShowStopsModal(false)}>
          <div className="modal modal-large" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <div className="modal-header">
              <h2>Manage Stops - {selectedRoute.name}</h2>
              <button className="modal-close" onClick={() => setShowStopsModal(false)} aria-label="Close">&times;</button>
            </div>

            <div className="modal-body stops-modal-body">
              {/* Current Stops */}
              <div className="stops-section">
                <div className="stops-section-header">
                  <h3 className="stops-section-title">
                    Current Stops ({selectedRoute.stops?.length || 0})
                  </h3>
                  {selectedRoute.stops?.length > 1 && (
                    <button
                      className="btn btn-sm btn-outline"
                      onClick={optimizeRouteOrder}
                      disabled={optimizing}
                    >
                      {optimizing ? 'Optimizing...' : 'Optimize Route'}
                    </button>
                  )}
                </div>
                {selectedRoute.stops?.length === 0 ? (
                  <p className="text-muted">No stops added yet. Add customers from the list below.</p>
                ) : (
                  <div className="stops-list">
                    {selectedRoute.stops?.map((stop, index) => (
                      <div key={stop.id} className="stop-item">
                        <div className="stop-order">{index + 1}</div>
                        <div className="stop-info">
                          <div className="stop-name">
                            {stop.first_name} {stop.last_name}
                          </div>
                          <div className="stop-address">{stop.address}, {stop.city}</div>
                          {stop.status === 'completed' && !isPendingDepthStop(stop) && stopDepthBand(stop) && (
                            <div className="stop-service-line">{snowServiceLabel(stopDepthBand(stop))}</div>
                          )}
                          {stop.status === 'completed' && !isPendingDepthStop(stop) && stopPremiumPercent(stop) && (
                            <div className="stop-service-line">
                              {premiumLineLabel(stopPremiumPercent(stop))}
                              {!shouldHidePrices() && parseFloat(stop.on_call_premium_amount) > 0 && ` ${formatCurrency(stop.on_call_premium_amount)}`}
                            </div>
                          )}
                        </div>
                        {isPendingDepthStop(stop) && (
                          <span className="awaiting-depth-badge">{AWAITING_DEPTH_LABEL}</span>
                        )}
                        {stop.invoice_stale === true && isAdmin() && (
                          <button
                            type="button"
                            className="btn btn-sm btn-outline bill-diff-btn"
                            disabled={billingDiffStopId === stop.id || !isPro()}
                            title={isPro() ? 'The price changed after this stop was invoiced' : 'Bill the difference needs the Pro plan'}
                            onClick={() => handleBillDifference(stop)}
                          >
                            {billingDiffStopId === stop.id ? 'Sending...' : 'Bill the difference'}
                          </button>
                        )}
                        {diffResults[stop.id] && (
                          <span className={`bill-diff-result ${diffResults[stop.id].ok ? 'ok' : 'err'}`}>
                            {diffResults[stop.id].message}
                          </span>
                        )}
                        {stop.status === 'completed' && (
                          <>
                            <span className={`payment-badge ${
                              stop.payment_status === 'paid' ? 'paid' :
                              stop.payment_status === 'invoice_sent' ? 'invoice-sent' : 'not-paid'
                            }`}>
                              {stop.payment_status === 'paid'
                                ? (methodLabel(paymentMethods, stop.payment_method) || 'Paid')
                                : stop.payment_status === 'invoice_sent'
                                ? 'Invoice Sent'
                                : 'Not Paid'}
                            </span>
                            {stop.payment_status !== 'paid' && !stop.is_comp && (
                              <span
                                className="mark-paid-link"
                                onClick={() => openPaymentModal(stop)}
                              >
                                Mark Paid
                              </span>
                            )}
                          </>
                        )}
                        {stop.is_comp && (
                          <span className="comp-badge" title={stop.comp_reason || 'Comp/Free'}>
                            COMP
                          </span>
                        )}
                        {stopPhotoCounts[stop.id] > 0 && (
                          <span
                            className="photo-count-badge clickable"
                            title={`${stopPhotoCounts[stop.id]} photo${stopPhotoCounts[stop.id] > 1 ? 's' : ''} - Click to view`}
                            onClick={() => setPhotoGalleryStopId(stop.id)}
                          >
                            📷 {stopPhotoCounts[stop.id]}
                          </span>
                        )}
                        <button
                          className="remove-stop-btn"
                          onClick={() => removeStopFromRoute(stop)}
                          disabled={stop.status === 'completed'}
                          title={stop.status === 'completed'
                            ? 'Completed stops cannot be removed. Archive the route instead.'
                            : 'Remove from route'}
                          aria-label={stop.status === 'completed'
                            ? 'Completed stop, cannot be removed'
                            : 'Remove stop from route'}
                        >
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="18" y1="6" x2="6" y2="18"></line>
                            <line x1="6" y1="6" x2="18" y2="18"></line>
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Available Customers */}
              <div className="stops-section">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                  <h3 className="stops-section-title">
                    Available Customers ({visible.length})
                  </h3>
                  {declinedCount > 0 && (
                    <label style={{ fontSize: '13px', display: 'flex', alignItems: 'center', gap: '4px', marginRight: 'auto' }}>
                      <input type="checkbox" checked={showDeclined} onChange={(e) => setShowDeclined(e.target.checked)} />
                      Show declined ({declinedCount})
                    </label>
                  )}
                  {notInRoute.length > 0 && (
                    <button
                      className="btn btn-sm btn-outline"
                      onClick={addAllToRoute}
                      disabled={addingAll}
                      title="Add every customer not on this route. Skips Do Not Serve, anyone who declined the season, and on-call customers who have not requested this day."
                    >
                      {addingAll ? 'Adding...' : 'Add all'}
                    </button>
                  )}
                </div>
                {stopsNotice && (
                  <p className="season-stops-notice" role="status">{stopsNotice}</p>
                )}
                {notInRoute.length === 0 ? (
                  <p className="text-muted">All customers have been added to this route.</p>
                ) : visible.length === 0 ? (
                  <p className="text-muted">Everyone left declined the season. Tick Show declined to see them.</p>
                ) : (
                  <div className="available-customers-list">
                    {visible.map((stopOption) => (
                      <div
                        key={stopOption.id}
                        className={`available-customer-item${stopOption.request.noRequest ? ' no-request' : ''}`}
                      >
                        <div className="customer-info">
                          <div className="customer-name">
                            {stopOption.displayName}
                            <span className={`service-type-badge ${preferenceBadge(stopOption.servicePreference).className}`}>
                              {preferenceBadge(stopOption.servicePreference).label}
                            </span>
                            {stopOption.snowfall_threshold && stopOption.snowfall_threshold !== 'any' && (
                              <span className="threshold-badge">{stopOption.snowfall_threshold}"+ snow</span>
                            )}
                            {stopOption.request.requestedDate && (
                              <span className="request-badge">{requestBadgeText(stopOption.request.requestedDate)}</span>
                            )}
                            {stopOption.request.noRequest && (
                              <span className="no-request-badge">{NO_REQUEST_LABEL}</span>
                            )}
                            {stopOption.doNotServe && (
                              <DoNotServeBadge reason={stopOption.doNotServeReason} inherited={stopOption.isChild} />
                            )}
                            {(stopOption.seasonStatus === 'declined' || stopOption.seasonStatus === 'pending') && (
                              <span className={seasonBadge({ season_status: stopOption.seasonStatus, season_key: stopOption.seasonKey }).className}>
                                {seasonBadge({ season_status: stopOption.seasonStatus, season_key: stopOption.seasonKey }).label}
                              </span>
                            )}
                          </div>
                          <div className="customer-address">{stopOption.address}, {stopOption.city}</div>
                        </div>
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={() => addStopToRoute(stopOption.id, stopOption)}
                        >
                          + Add
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn btn-primary" onClick={() => setShowStopsModal(false)}>
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .header-nav {
          display: flex;
          align-items: center;
          gap: 4px;
        }
        .nav-link {
          background: transparent;
          border: none;
          color: rgba(255,255,255,0.8);
          padding: 8px 12px;
          border-radius: 6px;
          cursor: pointer;
          font-size: 0.875rem;
          transition: all 0.15s ease;
        }
        .nav-link:hover {
          background: rgba(255,255,255,0.1);
          color: white;
        }
        .nav-link.active {
          background: rgba(255,255,255,0.2);
          color: white;
        }
        .storm-link {
          background: var(--color-secondary) !important;
          color: white !important;
        }
        .storm-link:hover {
          background: var(--color-secondary-dark) !important;
        }
        .admin-link {
          background: linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%) !important;
          color: white !important;
        }
        .admin-link:hover {
          background: linear-gradient(135deg, #6d28d9 0%, #5b21b6 100%) !important;
        }
        .logout-link {
          margin-left: 8px;
          border-left: 1px solid rgba(255,255,255,0.2);
          padding-left: 16px;
        }

        .flex-between {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          flex-wrap: wrap;
          gap: 16px;
        }

        .empty-state {
          text-align: center;
          padding: 48px 16px;
          color: var(--color-text-light);
        }
        .empty-state .btn {
          margin-top: 16px;
        }

        .routes-list {
          display: grid;
          gap: 16px;
        }

        .route-card {
          background: var(--color-card);
          border-radius: 12px;
          padding: 16px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.08);
        }

        .route-card-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 16px;
          flex-wrap: wrap;
        }

        .route-name {
          font-size: 1.25rem;
          font-weight: 600;
          margin: 0 0 8px 0;
        }

        .route-meta {
          display: flex;
          flex-wrap: wrap;
          gap: 8px 12px;
          align-items: center;
        }

        .status-badge {
          font-size: 0.75rem;
          padding: 4px 10px;
          border-radius: 12px;
          font-weight: 500;
        }
        .status-badge.not-started {
          background: #e5e7eb;
          color: #4b5563;
        }
        .status-badge.in-progress {
          background: #fef3c7;
          color: #92400e;
        }
        .status-badge.completed {
          background: #d1fae5;
          color: #065f46;
        }

        .stop-count {
          font-size: 0.875rem;
          color: var(--color-text-light);
        }

        .route-total {
          font-size: 1rem;
          font-weight: 700;
          color: var(--color-success);
          background: rgba(76, 175, 80, 0.1);
          padding: 4px 10px;
          border-radius: 6px;
        }
        .route-total-muted {
          color: #6b7280;
          background: #f3f4f6;
          font-weight: 600;
        }
        .driver-pay-badge {
          font-size: 0.9rem;
          font-weight: 600;
          color: #2196F3;
          background: rgba(33, 150, 243, 0.1);
          padding: 4px 10px;
          border-radius: 6px;
        }
        .calculated-total {
          font-size: 1.25rem;
          font-weight: 700;
          color: #059669;
          background: rgba(5, 150, 105, 0.1);
          padding: 10px 16px;
          border-radius: 8px;
        }
        .route-total-wrapper {
          display: flex;
          flex-direction: column;
          align-items: center;
        }
        /* Depth pricing: route chip, pending total, stop badge, bill the difference */
        .depth-chip {
          display: inline-flex;
          align-items: center;
          font-size: 0.75rem;
          font-weight: 600;
          padding: 3px 10px;
          border-radius: 12px;
          border: 1px solid transparent;
          line-height: 1.4;
          white-space: nowrap;
        }
        button.depth-chip { cursor: pointer; font-family: inherit; }
        .depth-chip-awaiting { background: #fef3c7; color: #92400e; border-color: #fcd34d; }
        .depth-chip-set { background: #f3f4f6; color: #374151; border-color: #d1d5db; }
        .depth-chip-empty { background: #fff; color: #6b7280; border-color: #d1d5db; border-style: dashed; }
        button.depth-chip-awaiting:hover { background: #fde68a; }
        button.depth-chip-set:hover, button.depth-chip-empty:hover { background: #e5e7eb; }
        .awaiting-depth-indicator {
          font-size: 0.75rem;
          color: #b45309;
          margin-left: 6px;
          font-weight: 500;
        }
        .awaiting-depth-badge {
          font-size: 0.7rem;
          font-weight: 600;
          padding: 2px 8px;
          border-radius: 10px;
          background: #fef3c7;
          color: #92400e;
          white-space: nowrap;
        }
        .bill-diff-btn { white-space: nowrap; font-size: 0.75rem; padding: 3px 8px; }
        .bill-diff-result { font-size: 0.75rem; max-width: 240px; }
        .bill-diff-result.ok { color: #166534; }
        .bill-diff-result.err { color: #b91c1c; }

        .skipped-indicator {
          font-size: 0.75rem;
          font-weight: 500;
          color: #6b7280;
        }

        /* Payment breakdown styles */
        .payment-breakdown {
          display: flex;
          align-items: center;
          gap: 4px;
          font-size: 0.8rem;
          font-weight: 500;
          padding: 3px 8px;
          border-radius: 4px;
          background: rgba(0, 0, 0, 0.03);
        }
        .payment-fully-paid {
          color: #10b981;
          font-weight: 600;
        }
        .payment-paid {
          color: #10b981;
        }
        .payment-unpaid {
          color: #dc2626;
        }
        .payment-separator {
          color: #d1d5db;
          margin: 0 2px;
        }

        .route-actions {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
        }

        .route-notes {
          margin: 12px 0 0;
          padding-top: 12px;
          border-top: 1px solid var(--color-border);
          font-size: 0.875rem;
          color: var(--color-text-light);
        }

        /* Form Styles */
        .form-divider {
          font-weight: 600;
          color: var(--color-text);
          margin: 20px 0 8px;
          padding-bottom: 8px;
          border-bottom: 1px solid var(--color-border);
        }
        .form-hint {
          font-size: 0.875rem;
          color: var(--color-text-light);
          margin: 0 0 12px 0;
        }
        .form-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 12px;
        }
        .form-row-3 {
          grid-template-columns: 2fr 1fr 1fr;
        }
        .checkbox-label {
          display: flex;
          align-items: center;
          gap: 8px;
          cursor: pointer;
          font-size: 0.875rem;
        }
        .checkbox-label input {
          width: 18px;
          height: 18px;
        }

        /* Modal Styles */
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0,0,0,0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 16px;
        }
        .modal {
          background: white;
          border-radius: 12px;
          max-width: 500px;
          width: 100%;
          max-height: 90vh;
          overflow-y: auto;
        }
        .modal-large {
          max-width: 700px;
        }
        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px 20px;
          border-bottom: 1px solid var(--color-border);
        }
        .modal-header h2 {
          margin: 0;
          font-size: 1.25rem;
        }
        .modal-close {
          background: none;
          border: none;
          font-size: 1.5rem;
          cursor: pointer;
          color: var(--color-text-light);
          line-height: 1;
        }
        .modal-body {
          padding: 20px;
        }
        .modal-footer {
          display: flex;
          justify-content: flex-end;
          gap: 12px;
          padding: 16px 20px;
          border-top: 1px solid var(--color-border);
        }

        /* Stops Modal */
        .stops-modal-body {
          max-height: 60vh;
          overflow-y: auto;
        }
        .stops-section {
          margin-bottom: 24px;
        }
        .stops-section:last-child {
          margin-bottom: 0;
        }
        .stops-section-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 12px;
        }
        .stops-section-title {
          font-size: 1rem;
          font-weight: 600;
          margin: 0;
          color: var(--color-text);
        }

        .stops-list {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
        .stop-item {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          padding: 12px;
          background: var(--color-background);
          border: 1px solid var(--color-border);
          border-radius: 8px;
        }
        .stop-order {
          width: 32px;
          height: 32px;
          background: var(--color-primary);
          color: white;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 600;
          font-size: 0.875rem;
          flex-shrink: 0;
        }
        .stop-info {
          flex: 1;
        }
        .stop-name {
          font-weight: 500;
        }
        .stop-address {
          font-size: 0.875rem;
          color: var(--color-text-light);
        }

        .stop-service-line {
          font-size: 0.8rem;
          color: var(--color-text-light);
        }

        /* Remove stop button - red X */
        .remove-stop-btn {
          width: 40px;
          height: 40px;
          min-width: 40px;
          border: 2px solid #dc2626;
          background: #fee2e2;
          color: #dc2626;
          border-radius: 50%;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.15s ease;
          flex-shrink: 0;
          margin-left: auto;
        }
        .remove-stop-btn:hover {
          background: #dc2626;
          color: white;
          transform: scale(1.1);
        }
        .remove-stop-btn:active {
          transform: scale(0.95);
        }
        .remove-stop-btn:disabled {
          opacity: 0.35;
          cursor: not-allowed;
          transform: none;
          background: transparent;
          color: inherit;
        }
        .header-actions {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
        }
        .route-dialog-list {
          max-height: 180px;
          overflow-y: auto;
          margin: 8px 0 12px;
          padding-left: 20px;
          font-size: 0.9rem;
        }
        .remove-stop-btn svg {
          width: 20px;
          height: 20px;
        }

        /* Payment Badge */
        .payment-badge {
          font-size: 0.7rem;
          padding: 3px 8px;
          border-radius: 10px;
          font-weight: 600;
          white-space: nowrap;
          flex-shrink: 0;
        }
        .payment-badge.paid {
          background: rgba(34, 197, 94, 0.15);
          color: #15803d;
        }
        .payment-badge.invoice-sent {
          background: rgba(234, 179, 8, 0.15);
          color: #a16207;
        }
        .payment-badge.not-paid {
          background: rgba(156, 163, 175, 0.15);
          color: #6b7280;
        }

        /* Photo Count Badge */
        .photo-count-badge {
          font-size: 0.7rem;
          padding: 3px 8px;
          border-radius: 10px;
          font-weight: 500;
          white-space: nowrap;
          flex-shrink: 0;
          background: rgba(59, 130, 246, 0.15);
          color: #2563eb;
        }
        .photo-count-badge.clickable {
          cursor: pointer;
          transition: background 0.15s, transform 0.1s;
        }
        .photo-count-badge.clickable:hover {
          background: rgba(59, 130, 246, 0.25);
          transform: scale(1.05);
        }

        /* Mark Paid Link */
        .mark-paid-link {
          font-size: 0.7rem;
          color: #22c55e;
          cursor: pointer;
          text-decoration: underline;
          margin-left: 4px;
        }
        .mark-paid-link:hover {
          color: #16a34a;
        }

        /* Payment Modal */
        .payment-modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 250;
          padding: 20px;
        }
        .payment-modal {
          background: white;
          border-radius: 12px;
          width: 100%;
          max-width: 400px;
          overflow: hidden;
        }
        .payment-modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px 20px;
          border-bottom: 1px solid #e2e8f0;
        }
        .payment-modal-header h3 {
          margin: 0;
          font-size: 1.1rem;
          font-weight: 600;
        }
        .modal-close {
          background: none;
          border: none;
          font-size: 1.5rem;
          cursor: pointer;
          color: #64748b;
          padding: 0;
          line-height: 1;
        }
        .payment-modal-body {
          padding: 20px;
        }
        .payment-customer {
          margin: 0 0 16px 0;
          font-size: 0.95rem;
        }
        .payment-address {
          color: #64748b;
        }
        .payment-amount {
          color: #059669;
          font-weight: 600;
        }
        .payment-modal .form-group {
          margin-bottom: 16px;
        }
        .payment-modal .form-group label {
          display: block;
          font-size: 0.85rem;
          font-weight: 500;
          margin-bottom: 6px;
          color: #374151;
        }
        .payment-modal .form-group select,
        .payment-modal .form-group input {
          width: 100%;
          padding: 10px 12px;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          font-size: 0.95rem;
        }
        .payment-modal-footer {
          display: flex;
          gap: 12px;
          padding: 16px 20px;
          border-top: 1px solid #e2e8f0;
          background: #f8fafc;
        }
        .payment-modal-footer .btn-cancel {
          flex: 1;
          padding: 10px;
          border: 1px solid #d1d5db;
          background: white;
          border-radius: 8px;
          font-size: 0.95rem;
          cursor: pointer;
        }
        .payment-modal-footer .btn-confirm {
          flex: 1;
          padding: 10px;
          border: none;
          background: #22c55e;
          color: white;
          border-radius: 8px;
          font-size: 0.95rem;
          font-weight: 600;
          cursor: pointer;
        }
        .payment-modal-footer .btn-confirm:hover {
          background: #16a34a;
        }
        .payment-modal-footer .btn-confirm:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .available-customers-list {
          display: flex;
          flex-direction: column;
          gap: 8px;
          max-height: 300px;
          overflow-y: auto;
        }
        .available-customer-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px;
          background: var(--color-background);
          border-radius: 8px;
        }
        .available-customer-item .customer-info {
          flex: 1;
        }
        .available-customer-item .customer-name {
          font-weight: 500;
        }
        .available-customer-item .customer-address {
          font-size: 0.875rem;
          color: var(--color-text-light);
        }

        /* Service Type Badges */
        .service-type-badge {
          font-size: 0.7rem;
          padding: 2px 8px;
          border-radius: 10px;
          font-weight: 600;
          margin-left: 8px;
          white-space: nowrap;
        }
        .available-customer-item.no-request {
          opacity: 0.55;
        }
        .request-badge,
        .no-request-badge {
          display: inline-block;
          margin-left: 6px;
          padding: 2px 6px;
          border-radius: 4px;
          font-size: 11px;
          font-weight: 600;
        }
        .request-badge {
          background: #dcfce7;
          color: #166534;
        }
        .no-request-badge {
          background: #e5e7eb;
          color: #374151;
        }
        .service-type-badge.seasonal {
          background: rgba(59, 130, 246, 0.15);
          color: #1d4ed8;
        }
        .service-type-badge.as-needed {
          background: rgba(249, 115, 22, 0.15);
          color: #c2410c;
        }
        .season-badge {
          font-size: 0.7rem;
          padding: 2px 8px;
          border-radius: 10px;
          margin-left: 8px;
          white-space: nowrap;
          border: 1px solid transparent;
        }
        .season-badge.season-declined { background: #fef2f2; color: #b91c1c; border-color: #fecaca; }
        .season-badge.season-pending { color: var(--color-text-light); border-color: var(--color-border); }
        .season-stops-notice {
          margin: 4px 0 8px;
          padding: 6px 10px;
          font-size: 0.85rem;
          background: #fffbeb;
          color: #92400e;
          border: 1px solid #fde68a;
          border-radius: 6px;
        }

        .threshold-badge {
          font-size: 0.7rem;
          padding: 2px 8px;
          border-radius: 10px;
          font-weight: 600;
          margin-left: 8px;
          white-space: nowrap;
          background: #e5e7eb;
          color: #374151;
        }

        /* Archive Toggle */
        .routes-controls {
          display: flex;
          flex-wrap: wrap;
          justify-content: space-between;
          align-items: center;
          gap: 12px;
          margin-bottom: 16px;
        }
        .archive-toggle {
          display: flex;
          flex-wrap: wrap;
          gap: 4px;
          background: var(--color-background);
          padding: 4px;
          border-radius: 8px;
          width: fit-content;
        }
        .sort-toggle {
          padding: 8px 14px;
          border: 2px solid var(--color-border);
          border-radius: 20px;
          background: var(--color-card);
          cursor: pointer;
          font-size: 0.85rem;
          font-weight: 500;
          color: var(--color-text-light);
          transition: all 0.2s ease;
          white-space: nowrap;
          flex-shrink: 0;
        }
        .sort-toggle:hover {
          border-color: var(--color-primary);
          color: var(--color-primary);
        }
        .toggle-btn {
          padding: 8px 16px;
          border: none;
          background: transparent;
          border-radius: 6px;
          font-size: 0.875rem;
          font-weight: 500;
          color: var(--color-text-light);
          cursor: pointer;
          transition: all 0.15s ease;
        }
        .toggle-btn.active {
          background: white;
          color: var(--color-primary);
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
        }
        .toggle-btn:hover:not(.active) {
          color: var(--color-text);
        }

        /* Archived Route */
        .route-card.archived {
          opacity: 0.7;
          background: #f9f9f9;
        }
        .archived-badge {
          font-size: 0.7rem;
          background: #6b7280;
          color: white;
          padding: 2px 8px;
          border-radius: 4px;
          margin-left: 8px;
          font-weight: 500;
          vertical-align: middle;
        }
        .storm-id-badge {
          font-size: 0.75rem;
          background: #3b82f6;
          color: white;
          padding: 2px 8px;
          border-radius: 4px;
          font-weight: 500;
        }
        .assigned-badge {
          font-size: 0.75rem;
          background: #8b5cf6;
          color: white;
          padding: 2px 8px;
          border-radius: 4px;
          font-weight: 500;
        }
        .assigned-badge.unassigned {
          background: #6b7280;
        }
        .driver-filter-select {
          padding: 6px 12px;
          border: 1px solid #444;
          border-radius: 6px;
          background: #2a2a2a;
          color: #e0e0e0;
          font-size: 0.9rem;
          cursor: pointer;
        }
        .driver-filter-select:hover {
          border-color: #666;
        }

        /* Mobile Menu */
        .mobile-nav {
          display: flex;
          flex-direction: column;
          padding: 8px 16px 16px;
          border-top: 1px solid rgba(255,255,255,0.1);
        }
        .mobile-nav-link {
          background: transparent;
          border: none;
          color: rgba(255,255,255,0.8);
          padding: 12px 8px;
          text-align: left;
          font-size: 1rem;
          cursor: pointer;
          border-radius: 6px;
          transition: all 0.15s ease;
        }
        .mobile-nav-link:hover, .mobile-nav-link.active {
          background: rgba(255,255,255,0.1);
          color: white;
        }
        .mobile-nav-link.storm-link {
          background: var(--color-secondary);
          color: white;
          margin-top: 8px;
        }
        .mobile-nav-link.admin-link {
          background: linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%);
          color: white;
          margin-top: 8px;
        }
        .mobile-nav-link.logout-link {
          margin-top: 8px;
          border-top: 1px solid rgba(255,255,255,0.1);
          padding-top: 16px;
        }

        @media (max-width: 900px) {
          .desktop-nav {
            display: none;
          }
        }

        @media (max-width: 768px) {
          .route-card-header {
            flex-direction: column;
          }
          .route-actions {
            width: 100%;
            flex-wrap: wrap;
          }
          .route-actions .btn {
            flex: 1;
            min-width: 80px;
          }
          /* Mobile payment breakdown - more compact */
          .payment-breakdown {
            font-size: 0.75rem;
            padding: 2px 6px;
          }
        }
      `}</style>

      {/* Delete Route: type the route name, as Admin does for a company */}
      {deleteDialog && (
        <div className="modal-overlay" onClick={() => !deleteDialog.busy && setDeleteDialog(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <div className="modal-header">
              <h2>Delete Route</h2>
              <button className="modal-close" onClick={() => setDeleteDialog(null)} aria-label="Close">&times;</button>
            </div>
            <div className="modal-body">
              <p>This cannot be undone.</p>
              <p>
                This permanently deletes <strong>{deleteDialog.route.name}</strong> and
                its {parseInt(deleteDialog.route.stop_count || 0, 10)} {parseInt(deleteDialog.route.stop_count || 0, 10) === 1 ? 'stop' : 'stops'}
                {parseInt(deleteDialog.route.skipped_count || 0, 10) > 0 ? ` (${deleteDialog.route.skipped_count} skipped)` : ''}.
                None of them is completed.
              </p>
              <div className="form-group">
                <label className="form-label" htmlFor="delete-route-confirm">
                  To confirm, type <strong>{deleteDialog.route.name}</strong> below:
                </label>
                <input
                  id="delete-route-confirm"
                  type="text"
                  className="form-input"
                  value={deleteDialog.text}
                  onChange={(e) => setDeleteDialog({ ...deleteDialog, text: e.target.value })}
                  placeholder="Type route name to confirm"
                  autoFocus
                />
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setDeleteDialog(null)} disabled={deleteDialog.busy}>
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={confirmDeleteRoute}
                disabled={deleteDialog.text !== deleteDialog.route.name || deleteDialog.busy}
              >
                {deleteDialog.busy ? 'Deleting...' : 'Delete Route'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* A route with completed stops is archived, never deleted */}
      {archiveSuggest && (
        <div className="modal-overlay" onClick={() => setArchiveSuggest(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <div className="modal-header">
              <h2>{archiveSuggest.route.is_archived ? 'Route Kept' : 'Archive Instead'}</h2>
              <button className="modal-close" onClick={() => setArchiveSuggest(null)} aria-label="Close">&times;</button>
            </div>
            <div className="modal-body">
              <p>
                <strong>{archiveSuggest.route.name}</strong> has
                {' '}{parseInt(archiveSuggest.route.completed_count || 0, 10) || 'completed'}
                {parseInt(archiveSuggest.route.completed_count || 0, 10) === 1 ? ' completed stop' : ' completed stops'} and
                cannot be deleted. Completed stops hold the prices and payments that customer
                balances, statements and reports are built from.
              </p>
              {archiveSuggest.route.is_archived ? (
                <p>It is already archived: hidden from the active list, with every record kept.</p>
              ) : (
                <p>Archiving hides it from the active list and keeps every stop, price and balance.</p>
              )}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setArchiveSuggest(null)}>
                {archiveSuggest.route.is_archived ? 'Close' : 'Cancel'}
              </button>
              {!archiveSuggest.route.is_archived && (
                <button type="button" className="btn btn-primary" onClick={archiveInstead}>
                  Archive Route
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Archive season */}
      {seasonDialog && (
        <div className="modal-overlay" onClick={() => !seasonDialog.busy && setSeasonDialog(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <div className="modal-header">
              <h2>Archive Season</h2>
              <button className="modal-close" onClick={() => setSeasonDialog(null)} aria-label="Close">&times;</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label" htmlFor="season-through">
                  Archive every route whose stops were all done on or before
                </label>
                <input
                  id="season-through"
                  type="date"
                  className="form-input"
                  value={seasonDialog.through || ''}
                  onChange={(e) => e.target.value && loadSeasonPreview(e.target.value)}
                  disabled={seasonDialog.busy}
                />
              </div>
              <p className="text-muted">
                Archiving only hides routes from the active list. Every stop, price and payment
                still counts in balances, statements and reports.
              </p>
              {seasonDialog.loading && <div className="loading-spinner"></div>}
              {seasonDialog.error && <div className="alert alert-error">{seasonDialog.error}</div>}
              {seasonDialog.preview && seasonDialog.preview.count === 0 && (
                <p>No routes to archive: every finished route up to this date is already archived.</p>
              )}
              {seasonDialog.preview && seasonDialog.preview.count > 0 && (
                <>
                  <p>
                    <strong>{seasonDialog.preview.count}</strong> {seasonDialog.preview.count === 1 ? 'route' : 'routes'} will be archived:
                  </p>
                  <ul className="route-dialog-list">
                    {seasonDialog.preview.routes.map((r) => (
                      <li key={r.id}>{r.name} ({r.completed_count} completed)</li>
                    ))}
                  </ul>
                  <div className="form-group">
                    <label className="form-label" htmlFor="season-confirm">
                      To confirm, type <strong>{seasonConfirmPhrase}</strong> below:
                    </label>
                    <input
                      id="season-confirm"
                      type="text"
                      className="form-input"
                      value={seasonDialog.text || ''}
                      onChange={(e) => setSeasonDialog({ ...seasonDialog, text: e.target.value })}
                      placeholder={seasonConfirmPhrase}
                      disabled={seasonDialog.busy}
                    />
                  </div>
                </>
              )}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setSeasonDialog(null)} disabled={seasonDialog.busy}>
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={confirmArchiveSeason}
                disabled={!seasonDialog.preview || seasonDialog.preview.count === 0
                  || seasonDialog.text !== seasonConfirmPhrase || seasonDialog.busy || seasonDialog.loading}
              >
                {seasonDialog.busy ? 'Archiving...' : 'Archive Routes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Photo Gallery Modal */}
      <PhotoGalleryModal
        stopId={photoGalleryStopId}
        isOpen={photoGalleryStopId !== null}
        onClose={() => setPhotoGalleryStopId(null)}
        allowDelete={isAdmin}
        title="Service Photos"
      />

      <DepthEntryModal
        show={!!depthModalRoute}
        route={depthModalRoute}
        routes={routes}
        onClose={() => setDepthModalRoute(null)}
        onSaved={() => loadData()}
      />

      {/* Payment Modal */}
      {paymentModal.open && paymentModal.stop && (
        <div className="payment-modal-overlay" onClick={() => setPaymentModal({ open: false, stop: null })}>
          <div className="payment-modal" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
            <div className="payment-modal-header">
              <h3>Mark as Paid</h3>
              <button className="modal-close" onClick={() => setPaymentModal({ open: false, stop: null })} aria-label="Close">&times;</button>
            </div>
            <div className="payment-modal-body">
              <p className="payment-customer">
                <strong>{paymentModal.stop.first_name} {paymentModal.stop.last_name}</strong>
                <br />
                <span className="payment-address">{paymentModal.stop.address}</span>
                {!shouldHidePrices() && paymentModal.stop.price && (
                  <span className="payment-amount"> - {formatCurrency(paymentModal.stop.price)}</span>
                )}
              </p>

              <div className="form-group">
                <label>Payment Method</label>
                <select
                  value={paymentForm.paymentMethod}
                  onChange={e => setPaymentForm(prev => ({ ...prev, paymentMethod: e.target.value }))}
                >
                  {markPaidOptions(paymentMethods, paymentModal.stop?.payment_method).map(option => (
                    <option key={option.id} value={option.id}>
                      {option.id === 'square' && isPendingDepthStop(paymentModal.stop) ? 'Square, invoice after depth' : option.name}
                    </option>
                  ))}
                </select>
              </div>

              {isPendingDepthStop(paymentModal.stop) && paymentForm.paymentMethod === 'square' && (
                <p className="text-muted">Recorded as Square, not paid. The invoice goes out after the storm depth is entered.</p>
              )}
              {isPendingDepthStop(paymentModal.stop) && paymentForm.paymentMethod !== 'square' && (
                <div className="form-group">
                  <label>Amount collected *</label>
                  <input
                    type="number"
                    inputMode="decimal"
                    step="0.01"
                    min="0.01"
                    value={paymentForm.amountCollected}
                    onChange={e => setPaymentForm(prev => ({ ...prev, amountCollected: e.target.value }))}
                    placeholder="0.00"
                  />
                  <small className="text-muted">This stop is awaiting the storm depth, so it has no price yet. Enter what the customer paid.</small>
                </div>
              )}
              {paymentError && <div className="alert alert-error" role="alert">{paymentError}</div>}

              <div className="form-group">
                <label>Payment Date</label>
                <input
                  type="date"
                  value={paymentForm.paymentDate}
                  onChange={e => setPaymentForm(prev => ({ ...prev, paymentDate: e.target.value }))}
                />
              </div>

              <div className="form-group">
                <label>Notes (optional)</label>
                <input
                  type="text"
                  value={paymentForm.paymentNotes}
                  onChange={e => setPaymentForm(prev => ({ ...prev, paymentNotes: e.target.value }))}
                  placeholder="e.g., Check #1234"
                />
              </div>
            </div>
            <div className="payment-modal-footer">
              <button
                className="btn-cancel"
                onClick={() => setPaymentModal({ open: false, stop: null })}
              >
                Cancel
              </button>
              <button
                className="btn-confirm"
                onClick={submitPayment}
                disabled={savingPayment}
              >
                {savingPayment ? 'Saving...' : 'Mark Paid'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Routes;
